'use client'

import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { createClient } from '@/shared/lib/supabase/client'
import { getCurrentUser } from '@/shared/lib/queryClient'
import { APP_REGISTRY, getAppById, enabledIdsFromMap } from '@/registry/apps'
import {
  writeEnabledModulesCookie,
  clearEnabledModulesCookie,
} from '@/shared/lib/enabledModulesCookie'
import { clearModuleFlagsCookie } from '@/shared/lib/moduleFlags'
import { useModuleFlagsStore } from '@/shared/stores/moduleFlagsStore'

interface UserModulePreferences {
  id: string
  user_id: string
  enabled_modules: Record<string, boolean>
  created_at: string
  updated_at: string
}

interface ModulePreferencesState {
  preferences: UserModulePreferences | null
  loading: boolean
  error: string | null
  saving: boolean

  fetchPreferences: () => Promise<void>
  setModuleEnabled: (moduleId: string, enabled: boolean) => Promise<void>
  toggleModule: (moduleId: string) => Promise<void>
  isModuleEnabled: (moduleId: string) => boolean
  reset: () => void
}

export const useModulePreferencesStore = create<ModulePreferencesState>()(
  subscribeWithSelector((set, get) => ({
    preferences: null,
    loading: false,
    error: null,
    saving: false,

    fetchPreferences: async () => {
      // Coalesce concurrent / back-to-back callers. Without this, multiple
      // mount-time effects (DashboardShell + explore-page actions + StrictMode
      // double-mount + auth state notifications) can race and overwrite each
      // other's local optimistic updates with stale GET results.
      if (get().loading) return
      set({ loading: true, error: null })
      const supabase = createClient()

      const user = await getCurrentUser()
      if (!user) {
        set({ loading: false, error: 'Not authenticated', preferences: null })
        return
      }

      // Try to get existing preferences
      const { data, error } = await supabase
        .from('user_module_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single()

      if (error && error.code !== 'PGRST116') {
        set({ loading: false, error: error.message })
        return
      }

      // If no preferences exist, create the first row. If the user signed up
      // from a specific app's URL, that app id was stamped into their signup
      // metadata — start them scoped to just that app (soft: changeable in
      // Settings). Otherwise default to all-enabled ({}).
      if (!data) {
        const signupApp = user.user_metadata?.signup_app
        const enabledModules: Record<string, boolean> =
          typeof signupApp === 'string' && getAppById(signupApp)
            ? Object.fromEntries(
                APP_REGISTRY.map((app) => [app.id, app.id === signupApp]),
              )
            : {}

        const { data: newPreferences, error: insertError } = await supabase
          .from('user_module_preferences')
          .insert({
            user_id: user.id,
            enabled_modules: enabledModules,
          })
          .select()
          .single()

        if (insertError) {
          // A concurrent request (another mount-time effect, a second tab, or
          // a prior session) may have created the row between our SELECT and
          // INSERT. Postgres rejects the duplicate with a unique-violation
          // (code 23505 / HTTP 409) — that's not a real failure: re-read the
          // row that now exists and use it instead of erroring out and letting
          // the caller spin.
          if (insertError.code === '23505') {
            const { data: existing, error: reselectError } = await supabase
              .from('user_module_preferences')
              .select('*')
              .eq('user_id', user.id)
              .single()

            if (!reselectError && existing) {
              writeEnabledModulesCookie(enabledIdsFromMap(existing.enabled_modules))
              set({ preferences: existing, loading: false })
              return
            }
          }

          // The session's user no longer exists (admin-deleted account, or a
          // stale auth cookie that survived a DB reset) — the FK to auth.users
          // fails (code 23503). We can never create this row, so don't leave a
          // dead session that callers keep retrying against: sign out and reset.
          if (insertError.code === '23503') {
            await supabase.auth.signOut()
            get().reset()
            return
          }

          set({ loading: false, error: insertError.message })
          return
        }

        writeEnabledModulesCookie(enabledIdsFromMap(newPreferences.enabled_modules))
        set({ preferences: newPreferences, loading: false })
        return
      }

      // Keep the cookie middleware reads (for the disabled-app guard) in sync
      // with the freshly-loaded preferences.
      writeEnabledModulesCookie(enabledIdsFromMap(data.enabled_modules))
      set({ preferences: data, loading: false })
    },

    setModuleEnabled: async (moduleId: string, enabled: boolean) => {
      const { preferences } = get()
      if (!preferences) return

      set({ saving: true, error: null })
      const supabase = createClient()

      const newEnabledModules = {
        ...preferences.enabled_modules,
        [moduleId]: enabled,
      }

      const { error } = await supabase
        .from('user_module_preferences')
        .update({ enabled_modules: newEnabledModules })
        .eq('id', preferences.id)

      if (error) {
        set({ saving: false, error: error.message })
        return
      }

      writeEnabledModulesCookie(enabledIdsFromMap(newEnabledModules))
      set({
        preferences: { ...preferences, enabled_modules: newEnabledModules },
        saving: false,
      })
    },

    toggleModule: async (moduleId: string) => {
      const currentValue = get().isModuleEnabled(moduleId)
      await get().setModuleEnabled(moduleId, !currentValue)
    },

    isModuleEnabled: (moduleId: string) => {
      const { preferences } = get()
      // Missing key (or still loading) falls back to the app's own default —
      // true for established apps, false for newly launched ones.
      const appDefault = getAppById(moduleId)?.defaultEnabled ?? true
      if (!preferences) return appDefault
      return preferences.enabled_modules[moduleId] ?? appDefault
    },

    reset: () => {
      clearEnabledModulesCookie()
      // The flag results were evaluated for this account's PostHog identity —
      // drop them with the same sign-out lifecycle as the enabled cookie.
      clearModuleFlagsCookie()
      useModuleFlagsStore.getState().reset()
      set({
        preferences: null,
        loading: false,
        error: null,
        saving: false,
      })
    },
  }))
)
